import React, { useState, useEffect } from 'react';
import { Save, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import { VfxConfig, VfxType, VfxIntensity, UpdateVfxConfigRequest } from '../../../types';
import { useUpdateVfxConfig } from '../../../hooks/useApi';

interface VfxConfigFormProps {
  config: VfxConfig | null;
  onSuccess?: () => void;
  scheduleId?: string;
  onScheduleUpdate?: (data: UpdateVfxConfigRequest) => Promise<void>;
  /** When saving via parent (e.g. schedule), pass true while API is in progress so button shows loading */
  externalSaving?: boolean;
}

const VFX_TYPES: { value: VfxType; label: string; description: string }[] = [
  { value: 'snow', label: 'Snow', description: 'Gentle falling snowflakes' },
  { value: 'confetti', label: 'Confetti', description: 'Colorful confetti pieces' },
  { value: 'fireworks', label: 'Fireworks', description: 'Exploding fireworks' },
  { value: 'bubbles', label: 'Bubbles', description: 'Rising bubbles' },
  { value: 'stars', label: 'Stars', description: 'Twinkling stars' },
  { value: 'particles_connect', label: 'Particles Connect', description: 'Connected particles forming a network' },
  { value: 'absorbers', label: 'Absorbers', description: 'Particles being absorbed into points' },
  { value: 'polygons', label: 'Polygons', description: 'Flying polygon shapes' },
  { value: 'emitters', label: 'Emitters', description: 'Continuously emitting particles' },
  { value: 'sparkles', label: 'Sparkles', description: 'Sparkling effect' },
];

const INTENSITY_OPTIONS: { value: VfxIntensity; label: string; description: string }[] = [
  { value: 'light', label: 'Light', description: '50% particles (subtle)' },
  { value: 'medium', label: 'Medium', description: '100% particles (balanced)' },
  { value: 'heavy', label: 'Heavy', description: '150% particles (prominent)' },
];

const VfxConfigForm: React.FC<VfxConfigFormProps> = ({ config, onSuccess, scheduleId, onScheduleUpdate, externalSaving = false }) => {
  const [formData, setFormData] = useState<VfxConfig>({
    enabled: false,
    type: null,
    intensity: null,
  });

  const updateConfig = useUpdateVfxConfig();

  useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (formData.enabled && (!formData.type || !formData.intensity)) {
      return;
    }

    const updateData: UpdateVfxConfigRequest = {
      vfx: {
        enabled: formData.enabled,
        ...(formData.enabled && {
          type: formData.type!,
          intensity: formData.intensity!,
        }),
      },
    };

    try {
      if (scheduleId && onScheduleUpdate) {
        await onScheduleUpdate(updateData);
      } else {
      await updateConfig.mutateAsync(updateData);
      }
      onSuccess?.();
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleToggle = (enabled: boolean) => {
    setFormData({
      enabled,
      type: enabled ? (formData.type || 'snow') : null,
      intensity: enabled ? (formData.intensity || 'medium') : null,
    });
  };

  if (!config) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Sparkles className="h-5 w-5 mr-2" />
          VFX Configuration
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <label className="text-sm font-medium">Enable VFX Effects</label>
              <p className="text-xs text-muted-foreground mt-1">
                Toggle to enable or disable visual effects on client websites
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => handleToggle(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {formData.enabled && (
            <>
              {/* VFX Type Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  VFX Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.type || ''}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as VfxType })}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a VFX type</option>
                  {VFX_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Intensity Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Intensity <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {INTENSITY_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`flex flex-col p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.intensity === option.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="intensity"
                        value={option.value}
                        checked={formData.intensity === option.value}
                        onChange={(e) => setFormData({ ...formData, intensity: e.target.value as VfxIntensity })}
                        className="sr-only"
                        required
                      />
                      <span className="font-medium text-sm">{option.label}</span>
                      <span className="text-xs text-muted-foreground mt-1">{option.description}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Preview Info */}
              {formData.type && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900">
                    <strong>Preview:</strong> {VFX_TYPES.find((t) => t.value === formData.type)?.description}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    Changes will appear on all client websites within 60 seconds after saving.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button
              type="submit"
              disabled={(updateConfig.isLoading || externalSaving) || (formData.enabled && (!formData.type || !formData.intensity))}
              className="flex items-center space-x-2"
            >
              {updateConfig.isLoading || externalSaving ? (
                <>
                  <LoadingSpinner />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save VFX Configuration</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default VfxConfigForm;

